<template>
  <div class="glass-frosted w-88.5 rounded-8xl py-4 relative max-h-70 box-border">
    <div class="flex items-center justify-between px-4">
      <div class="flex items-center gap-1">
        <span class="text-l-medium text-black-white">{{ $t('lighting.lightingSetting') }}</span>
        <Tooltip
          v-if="type === 'audio-rhythm'"
          :align-offset="-10"
          align="start"
          :content="$t('lighting.audioShareTooltip')"
        >
          <Icon icon="Function-Wireframe-Description" class="text-text-glass-hover size-4" />
        </Tooltip>
      </div>
      <template v-if="type === 'keyboard'">
        <div class="flex items-center gap-1.5">
          <Tooltip
            v-if="backlightSwitch"
            :content="backlightSwitchTooltip"
            :disabled="enabledBacklight === null"
            side="left"
            :side-offset="-8"
          >
            <div class="flex items-center">
              <Switch
                :model-value="enabledBacklight ?? false"
                :disabled="enabledBacklight === null"
                @update:model-value="(val) => emit('switchBacklight', val)"
              />
            </div>
          </Tooltip>
          <template v-if="canManageCustomLighting">
            <Button
              v-show="!deleteSwitch"
              color="text-b-w"
              varian="fill"
              shape="circle"
              size="icon-xs"
              @click="changeDeleteSwitch"
            >
              <Icon icon="Function-Wireframe-Delete" />
            </Button>
            <Button v-show="deleteSwitch" color="black" size="xs" @click="changeDeleteSwitch">
              {{ $t('common.Deselect') }}
            </Button>
          </template>
        </div>
      </template>
      <template v-if="type === 'side'">
        <Tooltip
          :content="sideLightingSwitchTooltip"
          :disabled="enabledSideLighting === null"
          side="left"
          :side-offset="-8"
        >
          <div class="flex items-center">
            <Switch
              :model-value="enabledSideLighting ?? false"
              :disabled="enabledSideLighting === null"
              @update:model-value="(val) => emit('switchSideLighting', val)"
            />
          </div>
        </Tooltip>
      </template>
      <template v-if="type === 'audio-rhythm'">
        <div class="flex items-center gap-2">
          <Button
            color="normal"
            size="sm"
            :active="audioRhythmStatus === 'active'"
            class="h-7"
            @click="emit('toggleAudioRhythm')"
          >
            {{ audioRhythmStatusLabel }}
          </Button>
        </div>
      </template>
    </div>
    <ScrollArea class="overflow-x-hidden max-h-50 mt-3 pl-4 pr-2 mr-2 h-full">
      <div
        v-if="type !== 'indicator'"
        class="grid grid-cols-[repeat(auto-fill,minmax(5rem,1fr))] gap-2 pr-1"
      >
        <Button
          v-if="type === 'keyboard' && canManageCustomLighting"
          class="gap-2 px-3 py-2 rounded-6xl cursor-pointer whitespace-nowrap"
          color="normal"
          :disabled="deleteSwitch"
          @click="openAddDialog"
        >
          <Icon icon="Function-Wireframe-Add11" class="size-4 shrink-0" />
        </Button>
        <Tooltip
          v-for="mode in effects"
          :key="mode.id"
          :side-offset="0"
          :content="getEffectName(mode.i18nKey)"
          :disabled="!nameOverflowMap[mode.id]"
        >
          <Button
            class="gap-1 px-3 py-2 rounded-6xl cursor-pointer whitespace-nowrap"
            :active="modelValue === mode.id"
            :disabled="disabledButton(mode)"
            color="normal"
            @mouseenter="updateNameOverflow(mode.id, $event)"
            @click="handleModeClick(mode)"
          >
            <Icon
              :icon="getIcon(mode)"
              class="size-4 shrink-0"
              :class="{ 'text-brand-orange': deleteSwitch && !disabledButton(mode) }"
            />
            <span data-effect-name class="text-xs font-medium truncate min-w-12">
              {{ getEffectName(mode.i18nKey) }}
            </span>
          </Button>
        </Tooltip>
      </div>
      <div v-if="type === 'indicator'" class="flex flex-col gap-2">
        <div
          v-for="effect in effects"
          :key="effect.id"
          :class="[
            'group flex items-center gap-3 px-4 py-3 h-24 rounded-4xl cursor-pointer',
            `${modelValue === effect.id ? 'bg-white' : 'bg-key'}`,
          ]"
          @click="handleChooseMode(effect)"
        >
          <Icon
            :icon="getIcon(effect)"
            :class="[
              'size-9 shrink-0 text-black transition-colors',
              `${modelValue === effect.id ? 'text-brand-blue2' : 'text-text-glass-default group-hover:text-text-glass-hover'}`,
            ]"
          />
          <div class="flex flex-col">
            <span
              :class="[
                'text-m-medium transition-colors',
                `${modelValue === effect.id ? 'text-brand-blue2' : 'text-text-glass-default group-hover:text-text-glass-hover'}`,
              ]"
            >
              {{ getEffectName(effect.i18nKey) }}
            </span>
            <span
              :class="[
                'text-s-medium transition-colors',
                `${modelValue === effect.id ? 'text-blue-dark-hover' : 'text-text-glass-default group-hover:text-text-glass-hover'}`,
              ]"
            >
              {{ getEffectName(effect?.tip || '') }}
            </span>
          </div>
        </div>
      </div>
    </ScrollArea>
  </div>

  <Dialog
    v-model:open="showAddModeDialog"
    :title="$t('lighting.customLighting.title')"
    description=""
  >
    <div class="flex flex-col">
      <Input v-model="customName" size="md" class="text-text-active"></Input>
      <p
        class="mb-4 text-wrap text-s-medium"
        :class="isNameOverLimit ? 'text-brand-red' : 'text-text-glass-default'"
      >
        {{ $t('lighting.customLighting.description') }}
      </p>
      <div>
        <RadioGroup v-model="staticType" class="flex items-center">
          <div
            v-for="item in staticOptions"
            :key="item.value"
            class="flex items-center text-black-white text-s-medium"
          >
            <RadioGroupItem
              type="circle"
              class="mr-2 cursor-pointer"
              :value="item.value"
              :label="item.label"
            />
            {{ item.label }}
          </div>
        </RadioGroup>
      </div>
    </div>
    <template #footer>
      <div class="flex gap-2 justify-end">
        <Button color="text-b-w" @click="showAddModeDialog = false">
          {{ $t('common.cancel') }}
        </Button>
        <Button color="text-b-w" :disabled="nameIsEmpty || isNameOverLimit" @click="handleAddMode">
          {{ $t('common.confirm2') }}
        </Button>
      </div>
    </template>
  </Dialog>
  <Dialog
    v-model:open="showDeletModeDialog"
    :title="$t('lighting.deleteCustom.title')"
    :description="$t('lighting.deleteCustom.description')"
  >
    <template #footer>
      <div class="flex gap-2 justify-end">
        <Button color="black" @click="showDeletModeDialog = false">
          {{ $t('common.cancel') }}
        </Button>
        <Button color="text-b-w" @click="deleteMode">{{ $t('common.delete') }}</Button>
      </div>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
  import type { Ref } from 'vue'
  import { computed, reactive, ref } from 'vue'
  import { useI18n } from 'vue-i18n'
  import {
    Dialog,
    Button,
    Icon,
    Input,
    RadioGroup,
    RadioGroupItem,
    ScrollArea,
    Switch,
    Tooltip,
  } from '@rapoo/element'
  import { useLocaleStore } from '@/stores'
  import { useDeviceStore } from '@/stores/device'
  import { useAdminOnly } from '@/composables/useAdminOnly'
  import { storeToRefs } from 'pinia'
  import {
    convertStrToASCII,
    type KeyboardLightingTarget,
    type LightingCapability,
    type LightingEffectDefinition,
  } from '@rapoo/device-sdk'

  /** 自定义灯效名称最大 UTF-8 字节数（对应 4 中文或 12 英文） */
  const CUSTOM_NAME_MAX_BYTES = 12

  const props = defineProps<{
    modelValue: number
    type: KeyboardLightingTarget
    /** null = 尚未从设备读到侧灯开关状态 */
    enabledSideLighting: boolean | null
    /** 是否展示按键灯开关（能力位） */
    backlightSwitch: boolean
    /** null = 尚未从设备读到按键灯开关状态 */
    enabledBacklight: boolean | null
    effects: LightingEffectDefinition[]
    audioRhythmStatus?: 'idle' | 'requesting' | 'active' | 'error'
    audioRhythmUsingMic?: boolean
  }>()
  const emit = defineEmits([
    'update:modelValue',
    'change',
    'customMode',
    'switchSideLighting',
    'switchBacklight',
    'toggleAudioRhythm',
  ])

  const { te } = useI18n()
  const { t } = useLocaleStore()
  const { canAddKeyboardLighting } = useAdminOnly()
  const { currentDevice } = storeToRefs(useDeviceStore())

  /** 是否展示新增/删除自定义灯效：管理员权限 + 设备 lighting.canAdd */
  const canManageCustomLighting = computed(() => {
    if (!canAddKeyboardLighting.value) return false
    const lightingCap = currentDevice.value?.manifest.capabilities.find(
      (item): item is LightingCapability => item.type === 'lighting',
    )
    return (lightingCap?.config as { canAdd?: boolean } | undefined)?.canAdd !== false
  })

  const getIcon = (mode: LightingEffectDefinition) => {
    if (deleteSwitch.value) {
      if (mode?.isCustom) {
        return 'Status-Wireframe-Close'
      }
    }
    return (
      {
        dynamic: 'LightEffect-Wireframe-DynamicLight',
        static: 'LightEffect-Wireframe-StaticLight',
        gradient: 'LightEffect-Wireframe-StaticLight',
        indicator: 'LightEffect-Wireframe-ChargeLight',
        ambient: 'LightEffect-Wireframe-DecorLight',
      }[mode.kind] || ''
    )
  }
  const getEffectName = (key: string) => (te(key) ? t(key) : key)

  /** 灯效名称是否因 truncate 溢出；仅溢出时启用 Tooltip */
  const nameOverflowMap = reactive<Record<number, boolean>>({})
  const updateNameOverflow = (id: number, event: MouseEvent) => {
    const root = event.currentTarget as HTMLElement | null
    const nameEl = root?.querySelector<HTMLElement>('[data-effect-name]')
    nameOverflowMap[id] = !!nameEl && nameEl.scrollWidth > nameEl.clientWidth
  }

  const audioRhythmStatusLabel = computed(() => {
    if (props.audioRhythmStatus === 'active') {
      return t('lighting.audioRhythmStatus.stop')
    }
    if (props.audioRhythmStatus === 'requesting') {
      return t('lighting.audioRhythmStatus.requesting')
    }
    return t('lighting.audioRhythmStatus.idle')
  })

  const backlightSwitchTooltip = computed(() =>
    props.enabledBacklight
      ? t('lighting.switchTooltip.turnOff')
      : t('lighting.switchTooltip.turnOn'),
  )
  const sideLightingSwitchTooltip = computed(() =>
    props.enabledSideLighting
      ? t('lighting.switchTooltip.turnOff')
      : t('lighting.switchTooltip.turnOn'),
  )

  // 模式操作
  const handleModeClick = (mode: LightingEffectDefinition) => {
    if (deleteSwitch.value) {
      openDeleteDialog(mode)
    } else {
      handleChooseMode(mode)
    }
  }

  // 选择模式
  const handleChooseMode = (mode: LightingEffectDefinition) => {
    emit('update:modelValue', mode.id)
    emit('change', mode)
  }

  // 删除自定义模式
  const deleteSwitch = ref(false)
  const showDeletModeDialog = ref(false)
  const deleteModeInfo = ref({}) as Ref<LightingEffectDefinition>
  const changeDeleteSwitch = () => {
    deleteSwitch.value = !deleteSwitch.value
  }
  const disabledButton = (mode: LightingEffectDefinition) => {
    if (deleteSwitch.value) {
      if (!mode?.isCustom) {
        return true
      }
    }
    return false
  }
  const openDeleteDialog = (mode: LightingEffectDefinition) => {
    deleteModeInfo.value = mode
    showDeletModeDialog.value = true
  }
  const deleteMode = () => {
    if (deleteModeInfo.value?.id != undefined) {
      emit(
        'customMode',
        {
          event: 'delete',
          type: deleteModeInfo.value.staticType,
          name: deleteModeInfo.value.i18nKey,
        },
        deleteModeInfo.value.id,
      )
    }
    showDeletModeDialog.value = false
  }

  // 添加自定义模式
  const showAddModeDialog = ref(false)
  const staticType = ref(1)
  const customName = ref('')
  const nameIsEmpty = computed(
    () => typeof customName.value === 'string' && customName.value.trim() === '',
  )
  /** 输入时按 UTF-8 字节长度校验（中文约 3 字节，英文/数字 1 字节） */
  const isNameOverLimit = computed(
    () => convertStrToASCII(customName.value).length > CUSTOM_NAME_MAX_BYTES,
  )
  const initCustomData = () => {
    staticType.value = 1
    customName.value = ''
  }
  const openAddDialog = () => {
    initCustomData()
    showAddModeDialog.value = true
  }
  const staticOptions = [
    { label: t('lighting.customLighting.singleColorRadio'), value: 1 },
    { label: t('lighting.customLighting.twoToneGradientRadio'), value: 2 },
  ]
  const handleAddMode = () => {
    if (nameIsEmpty.value || isNameOverLimit.value) return
    emit('customMode', {
      event: 'add',
      type: staticType.value,
      name: customName.value,
    })
    showAddModeDialog.value = false
  }
</script>
